// src/App.tsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Configuration from './pages/Configuration';
import Settings from './pages/Settings';
import Companies from './pages/Companies';
import ProtectedRoute from './components/common/ProtectedRoute';

interface RoleRouteProps {
  children: React.ReactElement;
  allowedRoles: string[];
}

const RoleRoute: React.FC<RoleRouteProps> = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        {/* Public route - Login */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes - require authentication */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            {/* Dashboard - all roles */}
            <Route index element={<Dashboard />} />

            {/* SystemAdmin only */}
            <Route
              path="companies"
              element={
                <RoleRoute allowedRoles={['SystemAdmin']}>
                  <Companies />
                </RoleRoute>
              }
            />

            {/* Company users only (CompanyAdmin and CompanyUser) */}
            <Route
              path="configuration"
              element={
                <RoleRoute allowedRoles={['CompanyAdmin', 'CompanyUser']}>
                  <Configuration />
                </RoleRoute>
              }
            />

            <Route
              path="settings"
              element={
                <RoleRoute allowedRoles={['CompanyAdmin']}>
                  <Settings />
                </RoleRoute>
              }
            />
          </Route>
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
};

export default App;
